import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import type { Filter } from '@/models/Filter';
import type { VenueTypeOptions } from '@/utils/filters/options/getVenueTypeOptions';
import { formatMessage } from '@/utils/translationHelper';
import type { Locale } from '@/i18n';
import ApplyResetWrapper from '@/components/filters/ApplyResetWrapper';
import VenueTypeFilter from '@/components/filters/VenueTypeFilter';

type Props = {
  appliedFilters: Filter;
  options: VenueTypeOptions;
  locale: Locale;
  labels: {
    title: string;
    selectAll: string;
    allFilters: string;
    resetAllFilters: string;
    cancelAllFilters: string;
    applyAllFilters: string;
    clear: string;
    apply: string;
  };
  setSortingLabel?: Dispatch<SetStateAction<string>>;
  setSortingType?: Dispatch<SetStateAction<string>>;
  sortOptions?: { label: string; value: string }[];
  sortType?: string;
  sort?: string;
  setSort?: Dispatch<SetStateAction<string>>;
  searchTerm: string;
  showShadow: boolean;
  setShowShadow: (value: boolean) => void;
  setIsRfpBannerVisible: (isRfpBannerVisible: boolean) => void;
  testID?: string;
  isSearchExpanded: boolean;
  setIsSearchExpanded: (v: boolean) => void;
};

export default function VenueTypeContainer({
  appliedFilters,
  options,
  locale,
  labels,
  sortOptions,
  setSort,
  setSortingType,
  setSortingLabel,
  sortType,
  sort,
  searchTerm,
  showShadow,
  setShowShadow,
  setIsRfpBannerVisible,
  testID,
  isSearchExpanded,
  setIsSearchExpanded
}: Props) {
  const { title, selectAll, allFilters, resetAllFilters, cancelAllFilters, applyAllFilters, clear, apply } = labels;
  const [selected, setSelected] = useState<Filter>(appliedFilters);
  const [isOpen, setIsOpen] = useState(false);
  const [filterCount, setFilterCount] = useState<number>(0);

  useEffect(() => {
    setSelected(appliedFilters);

    // Calculating filter count to be used on surface filter
    setFilterCount(appliedFilters.VENUE_TYPE?.length || 0);
  }, [appliedFilters]);

  return (
    <ApplyResetWrapper
      setIsSearchExpanded={setIsSearchExpanded}
      isSearchExpanded={isSearchExpanded}
      title={formatMessage(labels.title, locale, {
        count: filterCount
      })}
      selected={selected}
      setSelected={setSelected}
      appliedFilters={appliedFilters}
      filters={['VENUE_TYPE']}
      testID={testID}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      strings={{ allFilters, resetAllFilters, cancelAllFilters, applyAllFilters, clear, apply }}
      showShadow={showShadow}
      setSortingLabel={setSortingLabel}
      setSortingType={setSortingType}
      sortOptions={sortOptions}
      sortType={sortType}
      sort={sort}
      setSort={setSort}
      searchTerm={searchTerm}
      setIsRfpBannerVisible={setIsRfpBannerVisible}
      filterCount={filterCount}
    >
      <VenueTypeFilter
        options={options}
        selected={selected}
        setSelected={setSelected}
        showTitle={false}
        labels={{ title, selectAll, clear }}
        setShowShadow={setShowShadow}
        testID={testID}
      />
    </ApplyResetWrapper>
  );
}
